import {
  Component,
  OnInit,
  AfterViewChecked,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  forwardRef,
  Provider,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor, FormControl } from '@angular/forms';
import { ProfileSearchService } from '../search/profile-search.service';
import { ProfileService } from '../shared/profile.service';
import { ProfileMin } from '../shared/profile-min.model';
import { ProfileSearchParams } from '../search/profile-search-params.model';
import { ProfileSearchResult } from '../search/profile-search-result.model';
import { TranslationService } from '../../shared/translation/translation.service';
import { UtilService } from '../../shared/util/util.service';
import {
  NumberValueAccessor,
  CreateNumberValueAccessorProvider,
  CreateNumberValueValidationProvider,
} from '../../shared/widgets/value-accessors/number-value-accessor';

@Component({
  selector: 'wl-related-profile-selector',
  templateUrl: 'related-profile-selector.component.html',
  providers: [
    ProfileSearchService,
    ProfileService,
    CreateNumberValueAccessorProvider(RelatedProfileSelectorComponent),
    CreateNumberValueValidationProvider(RelatedProfileSelectorComponent),
  ],
  standalone: false
})
export class RelatedProfileSelectorComponent extends NumberValueAccessor {
  @Input() includeNoneValue: boolean = true;
  @Input() includeSelectValue: boolean = false;
  @Input() noneValueDisplay: string = '';
  @Input() relatedProfileId: number = 0;
  @Input() primaryRelationsOnly: boolean = true;
  @Input() disabled: boolean = false;

  //for validation to know if 0 an be allowed
  @Input() required: any;

  @Output() onProfileChanged = new EventEmitter<ProfileMin>();
  @Output() onProfilesLoaded = new EventEmitter<boolean>();

  public _profileSearchService: ProfileSearchService;
  public _profileService: ProfileService;
  public _translateService: TranslationService;
  public _utilService: UtilService;
  public _noneText: string;

  public profiles: Array<ProfileMin>;
  public selectedProfile: ProfileMin;
  public displayName: string;
  public isSearching: boolean;
  public searchParams: ProfileSearchParams;

  public constructor(
    profileSearchService: ProfileSearchService,
    profileService: ProfileService,
    translateService: TranslationService,
    utilService: UtilService
  ) {
    super();
    this._profileSearchService = profileSearchService;
    this._profileService = profileService;
    this._translateService = translateService;
    this._utilService = utilService;
    this._noneText = '';

    this.profiles = new Array<ProfileMin>();

    this.searchParams = new ProfileSearchParams();
    this.searchParams.RelatedProfileId = this.relatedProfileId;

    this.selectedProfile = new ProfileMin();

    this.displayName = '';
    this.isSearching = false;
  }

  //this overrides the base class function to set
  //what it means for this this control to be valid
  isValid = (): boolean => {
    if (this._utilService.isNotNullOrUndefined(this.required)) {
      if (this.value > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  ngOnChanges(changes: SimpleChanges) {
    this.loadProfiles();
  }

  modelChange(data: string) {
    this.emitOutputObject();
  }

  loadProfiles = (): void => {
    if (this._utilService.isNotNullAndGreaterThanZero(this.relatedProfileId)) {
      this.searchParams.RelatedProfileId = this.relatedProfileId;
    }

    this.searchParams.PrimaryRelationsOnly = this.primaryRelationsOnly;
    this.searchParams.PageNumber = 0;
    this.searchParams.PageSize = 0;
    this.searchParams.OrderBy = 'ASF,LastName,FirstName';

    if (this.relatedProfileId > 0) {
      this.isSearching = true;

      this._profileSearchService.search(this.searchParams).then((data: any) => {
        this.dataCallBack(data.Result);
        this.isSearching = false;
      });
    } else {
      //pass empty array
      let data = new Array<ProfileMin>();
      this.dataCallBack(data);
      this.isSearching = false;
    }
  };

  dataCallBack = (data: Array<ProfileMin>): void => {
    this.profiles = data;

    let profileIdInLoadedProfiles = this.profiles.some(p => p.ProfileId == this.value);

    this.dataAddNone(this.profiles).then((data: Array<ProfileMin>): void => {
      this.onProfilesLoaded.emit(profileIdInLoadedProfiles);
    });
  };

  dataAddNone = (data: Array<ProfileMin>): Promise<Array<ProfileMin>> => {
    if (this.includeSelectValue == true) {
      let selectItem: ProfileMin = new ProfileMin();
      selectItem.ProfileId = 0;
      selectItem.ReportName = this._utilService.toTitleCase(this._translateService.getInstant('common.select'));

      if (this.profiles.length && this.profiles[0].ReportName != selectItem.ReportName) {
        this.profiles.splice(0, 0, selectItem);
      }

      return Promise.resolve(this.profiles);
    } else if (this.includeNoneValue == true) {
      let noneItem: ProfileMin = new ProfileMin();
      noneItem.ProfileId = 0;

      if (this._utilService.isNotNullEmptyOrWhitespace(this.noneValueDisplay)) {
        this._noneText = this.noneValueDisplay;

        noneItem.ReportName = this._noneText;
        if (this.profiles.length && this.profiles[0].ReportName != noneItem.ReportName) {
          this.profiles.splice(0, 0, noneItem);
        }
        return Promise.resolve(this.profiles);
      } else {
        this._noneText = this._utilService.toTitleCase(this._translateService.getInstant('common.none'));
        noneItem.ReportName = this._noneText;
        if (this.profiles.length && this.profiles[0].ReportName != noneItem.ReportName) {
          this.profiles.splice(0, 0, noneItem);
        }
        return Promise.resolve(this.profiles);
      }
    } else {
      return Promise.resolve(this.profiles);
    }
  };

  emitOutputObject = (): void => {
    if (this.value > 0) {
      this._profileService.getProfileMin(this.value).then((data: ProfileMin) => {
        this.selectedProfile = data;
        this.setDisplayName();
        this.onProfileChanged.emit(this.selectedProfile);
      });
    } else {
      this.onProfileChanged.emit(new ProfileMin());
    }
  };

  public setDisplayName = (): void => {
    if (this._utilService.isNotNullOrUndefined(this.selectedProfile)) {
      this.displayName = this.selectedProfile.ReportName + ' (' + this.selectedProfile.ProfileId.toString() + ')';
    } else {
      this.displayName = '';
    }
  };
}
